import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";

interface DemandLogRow {
  id: number;
  tenant_id: number;
  event_type: string;
  jan: string | null;
  shopee_item_name: string | null;
  country_code: string | null;
  quantity: number;
  shopee_order_id: string | null;
  detected_at: string | null;
  product?: {
    id: number;
    jan: string;
    product_name: string;
    public_stock: number;
  } | null;
}

interface RankingRow {
  jan: string | null;
  shopee_item_name: string | null;
  total_quantity: number;
  occurrences: number;
  unique_tenants: number;
  first_seen: string | null;
  last_seen: string | null;
  country_breakdown: Record<string, number>;
  product: {
    id: number;
    product_name: string;
    public_stock: number;
  } | null;
}

interface SummaryResp {
  period: { from: string; to: string };
  top_unstocked_jans: RankingRow[];
  top_oos_jans: RankingRow[];
  totals: {
    out_of_catalog_events: number;
    out_of_stock_events: number;
    top_n: number;
  };
}

interface ListResp {
  demand_log: DemandLogRow[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
}

const EVENT_LABEL: Record<string, string> = {
  out_of_catalog: "取扱外",
  out_of_stock_skip: "在庫不足→スキップ",
  out_of_stock_partial: "在庫不足→部分発注",
};

function formatDate(s: string | null): string {
  return s ? new Date(s).toLocaleString("ja-JP") : "—";
}

function formatBreakdown(b: Record<string, number>): string {
  return Object.entries(b)
    .sort((a, b) => b[1] - a[1])
    .map(([cc, q]) => `${cc}:${q}`)
    .join(" / ");
}

/**
 * 管理者側: 需要ヒートマップ画面 (本実装)。
 *
 * 上段: ランキング集計 (取扱外 / 在庫不足)
 * 下段: 生ログ一覧 (フィルタ + ページング)
 */
export function AdminDemandLogPage() {
  // ---- 期間フィルタ ----
  const [from, setFrom] = useState<string>(""); // YYYY-MM-DD
  const [to, setTo] = useState<string>("");

  // ---- summary ----
  const [summary, setSummary] = useState<SummaryResp | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // ---- list ----
  const [rows, setRows] = useState<DemandLogRow[]>([]);
  const [eventType, setEventType] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pagination, setPagination] = useState<ListResp["pagination"] | null>(
    null,
  );
  const [listLoading, setListLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const periodQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return params.toString();
  }, [from, to]);

  const loadSummary = useCallback(() => {
    setSummaryLoading(true);
    setError(null);
    const qs = periodQuery();
    return api
      .get<SummaryResp>(`/admin/demand-log/summary${qs ? `?${qs}` : ""}`)
      .then((res) => setSummary(res.data))
      .catch((e) => setError(apiErrorMessage(e, "サマリ取得に失敗しました")))
      .finally(() => setSummaryLoading(false));
  }, [periodQuery]);

  const loadList = useCallback(() => {
    setListLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (eventType) params.set("event_type", eventType);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    params.set("page", String(page));
    params.set("per_page", "50");
    return api
      .get<ListResp>(`/admin/demand-log?${params.toString()}`)
      .then((res) => {
        setRows(res.data.demand_log);
        setPagination(res.data.pagination);
      })
      .catch((e) => setError(apiErrorMessage(e, "生ログ取得に失敗しました")))
      .finally(() => setListLoading(false));
  }, [eventType, from, to, page]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const RankingTable = ({ rows }: { rows: RankingRow[] }) => {
    if (rows.length === 0) {
      return <p className="text-sm text-gray-500">期間内に該当データなし</p>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left">順位</th>
              <th className="px-3 py-2 text-left">JAN</th>
              <th className="px-3 py-2 text-left">商品名 (Shopee 表記)</th>
              <th className="px-3 py-2 text-right">累計数量</th>
              <th className="px-3 py-2 text-right">出現回数</th>
              <th className="px-3 py-2 text-right">テナント数</th>
              <th className="px-3 py-2 text-left">国別</th>
              <th className="px-3 py-2 text-left">Wonect 商品</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={`${r.jan}-${idx}`} className="border-b last:border-b-0">
                <td className="px-3 py-2">{idx + 1}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.jan || "—"}</td>
                <td className="px-3 py-2">{r.shopee_item_name || "—"}</td>
                <td className="px-3 py-2 text-right font-semibold">
                  {r.total_quantity}
                </td>
                <td className="px-3 py-2 text-right">{r.occurrences}</td>
                <td className="px-3 py-2 text-right">{r.unique_tenants}</td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {formatBreakdown(r.country_breakdown)}
                </td>
                <td className="px-3 py-2 text-xs">
                  {r.product ? (
                    <span>
                      {r.product.product_name}
                      <span className="text-gray-400 ml-1">
                        (在庫 {r.product.public_stock})
                      </span>
                    </span>
                  ) : (
                    <span className="text-gray-400">未登録</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">需要ヒートマップ</h1>
      <p className="text-sm text-gray-600">
        パイロット顧客の Shopee で「Wonect が取扱っていない商品」「在庫不足だった商品」を
        全件蓄積しています。新規仕入候補・safety_stock 係数調整の参考材料に使ってください。
      </p>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* ---- 期間フィルタ ---- */}
      <div className="card p-4 flex gap-3 items-end flex-wrap text-sm">
        <label className="flex flex-col">
          <span className="label">From</span>
          <input
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setPage(1);
            }}
            className="input"
          />
        </label>
        <label className="flex flex-col">
          <span className="label">To</span>
          <input
            type="date"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setPage(1);
            }}
            className="input"
          />
        </label>
        <button
          onClick={() => {
            setFrom("");
            setTo("");
            setPage(1);
          }}
          className="btn-secondary text-xs"
        >
          期間クリア
        </button>
        <div className="text-xs text-gray-500 ml-auto">
          ※ 期間未指定なら直近 30 日
        </div>
      </div>

      {/* ---- ランキング ---- */}
      <section className="card p-4 space-y-2">
        <h2 className="font-semibold">
          🛒 仕入候補ランキング — 取扱外 (上位{" "}
          {summary?.totals.top_n ?? "—"} 件 / 合計{" "}
          {summary?.totals.out_of_catalog_events ?? "—"} 件のイベント)
        </h2>
        {summaryLoading ? (
          <p className="text-sm text-gray-500">読み込み中…</p>
        ) : (
          <RankingTable rows={summary?.top_unstocked_jans || []} />
        )}
      </section>

      <section className="card p-4 space-y-2">
        <h2 className="font-semibold">
          ⚠️ 在庫不足ランキング — 既存商品 (上位{" "}
          {summary?.totals.top_n ?? "—"} 件 / 合計{" "}
          {summary?.totals.out_of_stock_events ?? "—"} 件のイベント)
        </h2>
        {summaryLoading ? (
          <p className="text-sm text-gray-500">読み込み中…</p>
        ) : (
          <RankingTable rows={summary?.top_oos_jans || []} />
        )}
      </section>

      {/* ---- 生ログ ---- */}
      <section className="card p-4 space-y-3">
        <div className="flex justify-between items-end">
          <h2 className="font-semibold">📜 生ログ (フィルタ + ページング)</h2>
          <label className="text-sm">
            イベント種別:
            <select
              value={eventType}
              onChange={(e) => {
                setEventType(e.target.value);
                setPage(1);
              }}
              className="ml-2 input inline-block w-auto"
            >
              <option value="">すべて</option>
              <option value="out_of_catalog">取扱外</option>
              <option value="out_of_stock_skip">在庫不足→スキップ</option>
              <option value="out_of_stock_partial">在庫不足→部分発注</option>
            </select>
          </label>
        </div>

        {listLoading ? (
          <p className="text-sm text-gray-500">読み込み中…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">
            条件に合う需要ログはありません。
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left">検出</th>
                  <th className="px-3 py-2 text-left">種別</th>
                  <th className="px-3 py-2 text-left">JAN</th>
                  <th className="px-3 py-2 text-left">商品名</th>
                  <th className="px-3 py-2 text-left">国</th>
                  <th className="px-3 py-2 text-right">数量</th>
                  <th className="px-3 py-2 text-left">Wonect 商品</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-b-0">
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {formatDate(r.detected_at)}
                    </td>
                    <td className="px-3 py-2">
                      {EVENT_LABEL[r.event_type] || r.event_type}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {r.jan || "—"}
                    </td>
                    <td className="px-3 py-2">{r.shopee_item_name || "—"}</td>
                    <td className="px-3 py-2">{r.country_code || "—"}</td>
                    <td className="px-3 py-2 text-right">{r.quantity}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.product ? (
                        <>
                          {r.product.product_name}
                          <span className="text-gray-400 ml-1">
                            (在庫 {r.product.public_stock})
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-400">未登録</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.total_pages > 1 && (
          <div className="flex justify-between items-center text-xs text-gray-600">
            <div>
              全 {pagination.total} 件 / {pagination.page} ページ目 (全{" "}
              {pagination.total_pages} ページ)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs"
              >
                前へ
              </button>
              <button
                onClick={() =>
                  setPage((p) => Math.min(pagination.total_pages, p + 1))
                }
                disabled={page >= pagination.total_pages}
                className="btn-secondary text-xs"
              >
                次へ
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
